import React, { useState, useMemo } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IconSettings } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { ToolTypeEnum } from '@shared/constants/campaign.constants';
import {
  ToolDto,
  ToolRequestDto,
  AgentDto,
  useToolCreateControllerCreateAgentToolMutation,
  useToolUpdateControllerCreateAgentToolMutation,
  useToolDeleteControllerCreateAgentToolMutation,
} from '@shared/services/apiService/apiService';
import {
  McpToolFormData,
  McpToolRawJson,
  toolDtoToFormData,
  toolDtoToRawJson,
  headersArrayToObject,
} from '@pages/Admin/Campaign/ui/SidebarTabs/components/mcp.types';
import McpToolCard from '@pages/Admin/Campaign/ui/SidebarTabs/components/McpToolCard';
import McpDeleteConfirmDialog from '@pages/Admin/Campaign/ui/SidebarTabs/components/McpDeleteConfirmDialog';
import McpToolPopup from '@pages/Admin/Campaign/ui/SidebarTabs/components/McpToolPopup';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';

interface AgentMcpToolsSectionProps {
  agentId: string;
  agents: AgentDto[];
  tools: ToolDto[];
  refetch: () => void;
  disabled?: boolean;
}

const AgentMcpToolsSection: React.FC<AgentMcpToolsSectionProps> = ({
  agentId,
  agents,
  tools,
  refetch,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showError } = useErrorHandler();

  const [createAgentTool] = useToolCreateControllerCreateAgentToolMutation();
  const [updateAgentTool] = useToolUpdateControllerCreateAgentToolMutation();
  const [deleteAgentTool] = useToolDeleteControllerCreateAgentToolMutation();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<ToolDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<string | null>(null);

  // Filter MCP tools for this specific agent
  const agentMcpTools = useMemo(
    () => tools.filter((tool) => tool.type === ToolTypeEnum.Mcp && tool.agentId === agentId),
    [tools, agentId],
  );

  const existingTools = useMemo(
    () =>
      agentMcpTools
        .filter((tool) => tool.id !== editingTool?.id)
        .map((tool) => ({
          mcpName: tool.mcpName || '',
          agentId: tool.agentId,
          id: tool.id,
        })),
    [agentMcpTools, editingTool],
  );

  const handleAddNew = () => {
    setEditingTool(null);
    setIsPopupOpen(true);
  };

  const handleEdit = (tool: ToolDto) => {
    setEditingTool(tool);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setEditingTool(null);
  };

  const handleDeleteClick = (toolId: string) => {
    setToolToDelete(toolId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!toolToDelete) return;

    setIsLoading(true);
    try {
      await deleteAgentTool({
        id: agentId,
        toolId: toolToDelete,
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      refetch();
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    } catch (error) {
      showError({
        title: t('mcpTools.errors.deleteToolFailed'),
        error,
        onRetry: () => handleDeleteConfirm(),
        suggestion: t('mcpTools.errors.deleteSuggestion'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData: McpToolFormData) => {
    setIsLoading(true);
    try {
      const headersObject = headersArrayToObject(formData.mcpServerConfig.headers);

      const mcpToolRequest: ToolRequestDto = {
        type: ToolTypeEnum.Mcp,
        isAsync: true,
        mcpName: formData.mcpName.trim(),
        agentId: agentId,
        mcpServerConfig: {
          url: formData.mcpServerConfig.url.trim(),
          headers: headersObject,
          transportType: formData.mcpServerConfig.transportType,
          agentId: agentId,
        },
      };

      if (formData.id) {
        await updateAgentTool({
          id: agentId,
          toolId: formData.id,
          toolUpdateRequestDto: {
            isAsync: mcpToolRequest.isAsync,
            mcpName: mcpToolRequest.mcpName,
            agentId: mcpToolRequest.agentId,
            mcpServerConfig: mcpToolRequest.mcpServerConfig,
          },
        }).unwrap();
      } else {
        // Create new tool
        await createAgentTool({
          id: agentId,
          toolRequestDto: mcpToolRequest,
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      setIsPopupOpen(false);
      setEditingTool(null);
      refetch();
    } catch (error) {
      showError({
        title: formData.id
          ? t('mcpTools.errors.updateToolFailed')
          : t('mcpTools.errors.createToolFailed'),
        error,
        onRetry: () => handleSubmit(formData),
        suggestion: t('mcpTools.errors.toolOperationSuggestion'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRawJson = async (rawJson: McpToolRawJson) => {
    setIsLoading(true);
    try {
      const mcpToolRequest: ToolRequestDto = {
        type: ToolTypeEnum.Mcp,
        isAsync: true,
        mcpName: rawJson.mcpName.trim(),
        agentId: agentId,
        mcpServerConfig: {
          ...rawJson.mcpServerConfig,
          agentId: agentId,
        },
      };

      if (rawJson.id) {
        // Update existing tool
        await updateAgentTool({
          id: agentId,
          toolId: rawJson.id,
          toolUpdateRequestDto: {
            isAsync: mcpToolRequest.isAsync,
            mcpName: mcpToolRequest.mcpName,
            agentId: mcpToolRequest.agentId,
            mcpServerConfig: mcpToolRequest.mcpServerConfig,
          },
        }).unwrap();
      } else {
        await createAgentTool({
          id: agentId,
          toolRequestDto: mcpToolRequest,
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      setIsPopupOpen(false);
      setEditingTool(null);
      refetch();
    } catch (error) {
      showError({
        title: rawJson.id
          ? t('mcpTools.errors.updateToolFailed')
          : t('mcpTools.errors.createToolFailed'),
        error,
        onRetry: () => handleSubmitRawJson(rawJson),
        suggestion: t('mcpTools.errors.jsonOperationSuggestion'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            svg: {
              color: theme.palette.getContrastText(theme.palette.background.default),
            },
          }}>
          <IconSettings size={22} />
          <Typography fontSize="18px" fontWeight={600}>
            {t('mcpTools.title')}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          disabled={disabled || isLoading}>
          {t('mcpTools.addTool')}
        </Button>
      </Stack>

      {agentMcpTools.length > 0 ? (
        <Box>
          {agentMcpTools.map((tool) => (
            <McpToolCard
              key={tool.id}
              tool={tool}
              agents={agents}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              disabled={disabled || isLoading}
            />
          ))}
        </Box>
      ) : (
        <BorderedGrid
          sx={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: theme.palette.grey[50],
          }}>
          <Typography color="text.secondary">{t('mcpTools.emptyState')}</Typography>
        </BorderedGrid>
      )}

      <McpToolPopup
        open={isPopupOpen}
        onClose={handleClosePopup}
        initialData={editingTool ? toolDtoToFormData(editingTool) : null}
        initialRawJson={editingTool ? toolDtoToRawJson(editingTool) : null}
        agents={agents}
        existingTools={existingTools}
        onSubmit={handleSubmit}
        onSubmitRawJson={handleSubmitRawJson}
        isLoading={isLoading}
        disabled={disabled}
        fixedAgentId={agentId}
      />

      <McpDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default AgentMcpToolsSection;
